import { FC, useState } from 'react';

export interface AboutPageProps {
    name: string;
    logo: string;
    manifest: string;
    storeLink?: string;
    showDebugLink?: boolean;
}

export const AboutPage: FC<AboutPageProps> = ({ name, logo, manifest, storeLink, showDebugLink }) => {
    const [copyButtonLabel, setCopyButtonLabel] = useState('Copy');

    async function copyManifest() {
        await navigator.clipboard.writeText(manifest);
        setCopyButtonLabel('Copied!');
        setTimeout(() => setCopyButtonLabel('Copy'), 1500);
    }

    return (
        <div className="min-h-screen bg-white font-sans text-base leading-6 text-[rgba(38,50,56,.87)]">
            <header
                id="header"
                className="fixed inset-x-0 top-0 z-10 flex min-w-[300px] items-center justify-between border-b border-black/10 bg-white px-3 md:px-7"
            >
                <a
                    itemProp="url"
                    className="inline-flex items-center"
                    href="https://crowdin.com"
                    title="Crowdin - Localization Management Platform"
                >
                    <svg className="hidden md:block" width="180" height="54" aria-labelledby="crowdinLogoTitle">
                        <title id="crowdinLogoTitle">Crowdin</title>
                        <image href="/assets/img/crowdin.svg" width="180" height="54" />
                    </svg>
                    <svg className="block md:hidden" width="173" height="32" aria-labelledby="crowdinSmallLogoTitle">
                        <title id="crowdinSmallLogoTitle">Crowdin</title>
                        <image href="/assets/img/crowdin_small.svg" width="173" height="32" />
                    </svg>
                </a>
                <a
                    title="Contact"
                    href="https://crowdin.com/contacts"
                    className="ml-2 py-2 font-medium text-[rgba(38,50,56,.54)] hover:text-[#050c1a] hover:no-underline max-lg:text-sm"
                >
                    Contact
                </a>
            </header>
            <main className="pb-12 pt-16">
                <div className="mx-auto w-full max-w-[1140px] px-4">
                    <div className="mb-8 grid items-center gap-8 pt-24 text-base leading-6 text-[rgba(40,44,52,.87)] lg:grid-cols-[minmax(0,1fr)_minmax(0,4fr)] lg:px-[8.333333%]">
                        <div className="relative">
                            <div className="relative mx-auto h-0 w-[30%] rounded-2xl border border-black/10 pb-[30%] text-center lg:w-full lg:pb-[100%]">
                                <div className="absolute inset-0 flex items-center p-4">
                                    <img
                                        width="200"
                                        src={logo}
                                        alt={name}
                                        id="FeaturedImage-product-template"
                                        className="h-auto max-w-full"
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div>
                                <h1 className="mb-6 text-center text-[2.33333rem] leading-[1.2] font-semibold text-[#050c1a] lg:text-left">
                                    <span>{name}</span>
                                </h1>
                                <div className="text-sm">
                                    {name} extends the default functionality available in Crowdin and Crowdin Enterprise
                                    by providing additional capabilities to enhance your localization workflows.
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:px-[8.333333%]">
                        <div>
                            <p>To install this app, you can use either of the following methods: </p>
                            <ol className="list-decimal pl-10">
                                <li>
                                    Install it directly from the{' '}
                                    <a
                                        href="https://store.crowdin.com/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#66bb6a] hover:underline"
                                    >
                                        Crowdin Store
                                    </a>
                                    , if available.
                                </li>
                                <li>Install it manually using the manifest URL below.</li>
                            </ol>
                            <p>
                                For detailed steps, follow the{' '}
                                <a
                                    href="https://support.crowdin.com/developer/crowdin-apps-installation/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#66bb6a] hover:underline"
                                >
                                    App Installation
                                </a>
                            </p>

                            <div className="mb-3">
                                <label htmlFor="manifest-url" className="mb-2 block">
                                    Paste the following manifest URL when prompted during manual installation:
                                </label>
                                <div className="flex">
                                    <input
                                        type="text"
                                        className="min-w-0 flex-1 rounded-l border border-r-0 border-[#ced4da] px-3 py-1.5 text-base leading-6 text-[#212529] outline-none focus:z-[1] focus:border-[#86b7fe] focus:ring-4 focus:ring-[#0d6efd]/25"
                                        id="manifest-url"
                                        value={manifest}
                                        readOnly
                                    />
                                    <button
                                        className="rounded-r border border-[#6c757d] px-3 py-1.5 text-base leading-6 text-[#6c757d] transition-colors hover:bg-[#6c757d] hover:text-white focus:z-[1] focus:ring-4 focus:ring-[#6c757d]/25"
                                        type="button"
                                        id="copy-button"
                                        onClick={copyManifest}
                                    >
                                        {copyButtonLabel}
                                    </button>
                                </div>
                            </div>

                            {storeLink && (
                                <div>
                                    Read more about{' '}
                                    <a
                                        href={storeLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={`${name} on Crowdin Store`}
                                        className="text-[#66bb6a] hover:underline"
                                    >
                                        {name}
                                    </a>
                                    .
                                </div>
                            )}

                            {showDebugLink && (
                                <div className="mt-4 border-t border-black/10 pt-3">
                                    <p className="mb-2">
                                        <strong>Developer Tools</strong>
                                    </p>
                                    <p>
                                        Access the{' '}
                                        <a
                                            href="/__debug"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Open developer console in a new tab"
                                            className="text-[#66bb6a] hover:underline"
                                        >
                                            Developer Console
                                        </a>{' '}
                                        to inspect requests, debug issues, and monitor application behavior.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};
